import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { DEAL_TYPES, DEAL_SPECS, KPI_LABELS } from "@/lib/dealSpecs";
import { fmtBy } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const NAVY = "#0F2747";
const ICE = "#DCEEFF";
const SLATE = "#2A2E33";

export default function DealNew() {
    const [step, setStep] = useState(1);
    const [dealType, setDealType] = useState("");
    const [name, setName] = useState("");
    const [inputs, setInputs] = useState({});
    const [results, setResults] = useState({});
    const [sp] = useSearchParams();
    const propertyId = sp.get("property_id");
    const nav = useNavigate();

    const spec = DEAL_SPECS[dealType] || [];
    const kpis = KPI_LABELS[dealType] || [];

    // Seed defaults when deal type chosen
    useEffect(() => {
        if (!dealType) return;
        const defaults = {};
        (DEAL_SPECS[dealType] || []).forEach((sec) => sec.fields.forEach((f) => { defaults[f.key] = f.default; }));
        setInputs(defaults);
    }, [dealType]);

    // Debounced live calculate
    useEffect(() => {
        if (!dealType) return;
        const t = setTimeout(async () => {
            try {
                const { data } = await api.post("/deals/calculate", { deal_type: dealType, inputs });
                setResults(data.results);
            } catch {
                /* ignore */
            }
        }, 250);
        return () => clearTimeout(t);
    }, [inputs, dealType]);

    const chartData = useMemo(() => {
        if (!results?.sources_uses) return { sources: [], uses: [] };
        return {
            sources: Object.entries(results.sources_uses.sources || {}).map(([k, v]) => ({ name: k, value: v })),
            uses: Object.entries(results.sources_uses.uses || {}).map(([k, v]) => ({ name: k, value: v })),
        };
    }, [results]);

    const submit = async () => {
        if (!name || !dealType) return toast.error("Name and deal type required");
        try {
            const { data } = await api.post("/deals", { name, deal_type: dealType, inputs, property_id: propertyId || null });
            toast.success("Deal saved");
            nav(`/app/deals/${data.id}`);
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    };

    if (step === 1) {
        return (
            <div className="p-6 md:p-10" data-testid="page-deal-new-step1">
                <Link to="/app/deals" className="text-xs text-muted-foreground hover:opacity-70">← Back to deals</Link>
                <div className="overline mt-4">Forge Vision · Step 1 of 2</div>
                <h1 className="mt-2 font-display text-4xl tracking-tight leading-none">Choose a deal type</h1>
                <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {DEAL_TYPES.map((d) => (
                        <button key={d.key} onClick={() => { setDealType(d.key); setStep(2); }} className="text-left border border-border p-6 hover:border-navy/70 transition-colors" data-testid={`deal-type-${d.key}`}>
                            <div className="chip">{d.key.replace(/_/g, " ")}</div>
                            <div className="mt-3 font-display text-2xl tracking-tight">{d.label}</div>
                            <div className="mt-2 text-sm text-muted-foreground">{d.tagline}</div>
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 grid lg:grid-cols-2 gap-8" data-testid="page-deal-new-step2">
            <div>
                <button onClick={() => setStep(1)} className="text-xs text-muted-foreground hover:opacity-70">← Change deal type</button>
                <div className="overline mt-3">{DEAL_TYPES.find((d) => d.key === dealType)?.label}</div>
                <h1 className="mt-2 font-display text-3xl tracking-tight leading-none">New deal</h1>
                <div className="mt-6">
                    <Label>Deal name</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 123 Ridgeline · Fix & Flip" className="mt-2" data-testid="deal-name" />
                </div>
                <div className="mt-6 space-y-6">
                    {spec.map((section) => (
                        <div key={section.title} data-testid={`deal-section-${section.title.toLowerCase()}`}>
                            <div className="overline mb-3">{section.title}</div>
                            <div className="grid grid-cols-2 gap-3">
                                {section.fields.map((f) => (
                                    <div key={f.key}>
                                        <Label className="text-xs">{f.label}</Label>
                                        <Input
                                            type={f.type === "text" ? "text" : "number"}
                                            value={inputs[f.key] ?? ""}
                                            onChange={(e) => setInputs({ ...inputs, [f.key]: e.target.type === "number" ? Number(e.target.value) : e.target.value })}
                                            className="mt-1 text-sm"
                                            data-testid={`deal-input-${f.key}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-8 pt-6 border-t border-border">
                    <button onClick={submit} className="bg-foreground text-background px-6 py-3 text-sm hover:opacity-90" data-testid="deal-save">Save deal</button>
                </div>
            </div>
            <div className="lg:sticky lg:top-20 lg:self-start space-y-4">
                <div className="border border-border p-6" data-testid="deal-live-preview">
                    <div className="overline mb-3">Live preview</div>
                    <div className="grid grid-cols-2 gap-3">
                        {kpis.map((k) => (
                            <div key={k.key} className="border border-border p-3">
                                <div className="overline">{k.label}</div>
                                <div className="mt-1 text-lg font-medium">{fmtBy(k.fmt, results?.[k.key])}</div>
                            </div>
                        ))}
                    </div>
                </div>
                {chartData.uses.length > 0 && (
                    <div className="border border-border p-6" data-testid="deal-chart-uses">
                        <div className="overline mb-3">Uses of funds</div>
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={chartData.uses}>
                                <XAxis dataKey="name" fontSize={10} />
                                <YAxis fontSize={10} />
                                <Tooltip />
                                <Bar dataKey="value" fill={NAVY} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
                {chartData.sources.length > 0 && (
                    <div className="border border-border p-6" data-testid="deal-chart-capital-stack">
                        <div className="overline mb-3">Capital stack</div>
                        <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                                <Pie data={chartData.sources} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={80}>
                                    {chartData.sources.map((_, i) => <Cell key={i} fill={i === 0 ? SLATE : ICE} />)}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
}
